import React, { useState } from 'react';
import { getClassUpdateForLevel, generatePersonalizedUpdate, ClassUpdate } from '../data/classUpdateTemplates';
import { ClassAttendanceData } from '../services/ParentSessionService';
import '../styles/WeeklyClassCarousel.css';

interface WeeklyClassCarouselProps {
  studentFirstName: string;
  sessions: ClassAttendanceData[]; // Array of recent class sessions
}

const WeeklyClassCarousel: React.FC<WeeklyClassCarouselProps> = ({
  studentFirstName,
  sessions
}) => {
  // Start with the most recent (rightmost) session - index is sessions.length - 1
  const [currentSlide, setCurrentSlide] = useState(sessions.length - 1);

  if (sessions.length === 0) {
    return null; // No sessions to show
  }

  const currentSession = sessions[currentSlide];
  const template = getClassUpdateForLevel(currentSession.levelNumber);

  if (!template) {
    return null; // No template found for this level
  }

  // Get classmate names from the session
  const teammates = currentSession.attendees.map(a => a.studentName);

  // Generate content based on whether student was present
  let summary: string;
  let highlights: string[];

  if (currentSession.wasPresent) {
    const personalized = generatePersonalizedUpdate(
      template,
      studentFirstName,
      teammates,
      currentSession.starsEarned,
      currentSession.levelNumber
    );
    summary = personalized.summary;
    highlights = personalized.highlights;
  } else {
    // Student was absent - show "we missed you" message
    summary = `We missed ${studentFirstName} in class! While away, the class kept the rhythm going with ${template.levelTitle.toLowerCase()}.`;
    highlights = [
      `📚 Class explored ${template.levelTitle.toLowerCase()}`,
      `👥 ${teammates.length > 0 ? teammates.slice(0, 3).join(', ') : 'Their classmates'} kept learning`,
      `💫 Can't wait to see ${studentFirstName} next time!`
    ];
  }

  const finalVideoUrl = template.videoUrl;
  const hasVideo = finalVideoUrl && finalVideoUrl.trim() !== '';

  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sessions.length);
  };

  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sessions.length) % sessions.length);
  };

  // Format the date for the header (e.g., "Sep. 8th's Class!")
  const formatClassDate = (date: Date): string => {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const suffix = getDaySuffix(day);
    return `${month} ${day}${suffix}'s Class!`;
  };

  const getDaySuffix = (day: number): string => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  return (
    <div className="weekly-class-carousel">
      <div className="carousel-header">
        <h3>🎓 Last Class! ({formatClassDate(currentSession.sessionDate).replace("'s Class!", "")}): {template.levelTitle}</h3>
      </div>

      <div className={`carousel-content ${!hasVideo ? 'centered' : ''}`}>
        {/* Video Section - Only show if video URL exists */}
        {hasVideo && (
          <div className="carousel-video">
            <div className="video-container">
              <iframe
                src={finalVideoUrl}
                title={`${template.levelTitle} Class Video`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="video-iframe"
              />
            </div>
          </div>
        )}

        {/* Update Section - Right Side (or Centered if no video) */}
        <div className="carousel-update">
          <p className="update-summary">{summary}</p>

          <div className="update-highlights">
            <h5>{currentSession.wasPresent ? `${studentFirstName}'s Progress:` : 'What Happened:'}</h5>
            <ul>
              {highlights.map((highlight, index) => (
                <li key={index}>{highlight}</li>
              ))}
            </ul>
          </div>

          {currentSession.wasPresent && (
            <div className="conversation-starter">
              <strong>💬 Question to ask {studentFirstName}:</strong>
              <p>{template.conversationStarter}</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation - show only if there are multiple sessions */}
      {sessions.length > 1 && (
        <div className="carousel-navigation">
          <button onClick={goToPrevSlide} className="nav-arrow" aria-label="Previous class">←</button>
          <div className="nav-dots">
            {sessions.map((session, index) => {
              const shortDate = session.sessionDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              });
              return (
                <button
                  key={index}
                  className={`nav-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(index)}
                  aria-label={`Go to ${shortDate} class`}
                >
                  {shortDate}
                </button>
              );
            })}
          </div>
          <button onClick={goToNextSlide} className="nav-arrow" aria-label="Next class">→</button>
        </div>
      )}

      {/* Progress bar showing sequence */}
      {sessions.length > 1 && (
        <div className="session-progress">
          <div className="progress-track">
            {sessions.map((_, index) => (
              <div
                key={index}
                className={`progress-segment ${index <= currentSlide ? 'completed' : ''}`}
                style={{ width: `${100 / sessions.length}%` }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyClassCarousel;
